<script lang="ts" setup>
import type { SearchResponse } from '@algolia/client-search';
import { computed, onMounted, ref, watch } from 'vue';

import { AlgoliaSuggestedSearch, searchSuggestions } from '@/api/algolia';
import Header4 from '@/components/base/typography/Header4.vue';
import Header6 from '@/components/base/typography/Header6.vue';
import { useCallback } from '@/composables/useCallback';
import { NutsProduct } from '@/utils/product';

const props = defineProps<{ product: NutsProduct }>();

const suggestedSearches = ref<SearchResponse<AlgoliaSuggestedSearch>>();

const loadSuggestedSearches = useCallback(async () => {
  suggestedSearches.value = await searchSuggestions({
    distinct: true,
    optionalWords: props.product.name,
    hitsPerPage: 15,
    query: props.product.name,
  });
});

const suggestedSearchesFiltered = computed(() =>
  suggestedSearches.value?.hits
    .filter((item: AlgoliaSuggestedSearch) => {
      let result = item.query;
      props.product.name
        .replaceAll(/[^A-Za-z\s]/g, '')
        .toLowerCase()
        .split(' ')
        .forEach((word) => {
          result = result.replace(word, '');
        });
      const rest = result.replaceAll(' ', '');
      return rest.length > 0;
    })
    .slice(0, 5),
);

const relatedProductSearchesDataPromo = () =>
  suggestedSearchesFiltered.value
    ?.map((suggested) => suggested.query)
    .join(', ')
    .slice(0, -3);

onMounted(() => {
  loadSuggestedSearches.execute();
  watch(() => props.product.name, loadSuggestedSearches.execute);
});
</script>

<template>
  <div v-if="suggestedSearchesFiltered?.length">
    <Header4 headerTag="h3">Related Product Searches</Header4>
    <div class="grid grid-cols-2 gap-2 mt-4 lg:grid-cols-5">
      <RouterLink
        v-for="suggestion in suggestedSearchesFiltered"
        :key="suggestion.query"
        class="inline-flex items-center justify-center px-4 bg-white border border-solid rounded-2xl h-11 border-nuts-neutral-200 hover:bg-zinc-50 color-inherit"
        data-promo="1"
        :data-promo-creative="`Related Product Searches: ${relatedProductSearchesDataPromo()}`"
        data-promo-name="Merchandising"
        :to="`/search/instant?query=${suggestion.query}`"
      >
        <Header6
          class="overflow-hidden font-semibold capitalize whitespace-nowrap focus:no-underline hover:no-underline"
          headerTag="span"
        >
          {{ suggestion.query }}
        </Header6>
      </RouterLink>
    </div>
  </div>
</template>
